/**
 * ChernobylImpactModal.tsx — R20-GD-9 Chernobyl Incident impact summary.
 *
 * Shown after Chernobyl_Incident fires and at least one Nuclear facility is
 * decommissioned (50% chance per facility). Displays per-player impact:
 * how many plants were lost and tokens refunded (50% of built cost per plant).
 *
 * VISIBLE WHEN: pendingChernobylImpact !== null
 * Dismissed by dismissChernobylImpact() → clears pendingChernobylImpact.
 *
 * No boardAnimationActive gate — Chernobyl is a playCard effect that resolves
 * before boardAnimation, so this modal fires in "landed" or "idle" phase where
 * board animation is already false.
 */

import type React from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./ChernobylImpactModal.module.css";

const SEAT_LABELS = ["P1", "P2", "P3", "P4"] as const;

export function ChernobylImpactModal(): React.ReactElement | null {
  const pendingChernobylImpact = useGameStore((s) => s.pendingChernobylImpact);
  const dismissChernobylImpact = useGameStore((s) => s.dismissChernobylImpact);

  if (pendingChernobylImpact === null) return null;

  const { perSeat, totalLost } = pendingChernobylImpact;

  return (
    <dialog className={styles.overlay} aria-label="Chernobyl Incident Impact" open>
      <div className={styles.panel}>
        <div className={styles.icon}>&#9762;</div>
        <h2 className={styles.title}>CHERNOBYL INCIDENT</h2>
        <p className={styles.subtitle}>Nuclear meltdown — the board is shaken</p>

        {totalLost === 0 ? (
          <p className={styles.noImpact}>
            All nuclear facilities survived this time. The world breathes.
          </p>
        ) : (
          <div className={styles.summary}>
            <div className={styles.summaryTotal}>
              {totalLost} nuclear plant{totalLost !== 1 ? "s" : ""} decommissioned
            </div>
            {perSeat.length > 0 && (
              <ul className={styles.seatList}>
                {perSeat.map((row) => (
                  <li key={row.seat} className={styles.seatRow}>
                    <span className={styles.seatLabel}>
                      {SEAT_LABELS[row.seat] ?? `P${row.seat + 1}`}
                    </span>
                    <span className={styles.seatLost}>
                      -{row.lost} plant{row.lost !== 1 ? "s" : ""}
                    </span>
                    {row.refunded > 0 && (
                      <span className={styles.seatRefund}>+{row.refunded}T refund</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.continueBtn} onClick={dismissChernobylImpact}>
            CONTINUE
          </button>
        </div>
      </div>
    </dialog>
  );
}
